import React, { useState, useEffect, useMemo, useCallback, createContext } from 'react'
import PropTypes from 'prop-types'

import { getTikTokAccounts, revokeAccountConnection } from 'services/api/tikTokApi'
import useGetWabaList from 'queries/use-get-waba-list'
import useGetActivatedAccountsList from 'queries/use-get-actived-account-list'
import * as api from '../services/api/accountApi'
import { getSettings } from '../services/api/modelsApi'

export const AccountsContext = createContext()

function AccountsStateProvider({ children }) {
  const [brokers, setBrokers] = useState()
  const [channels, setChannels] = useState([])
  const [departments, setDepartments] = useState([])
  const [accounts, setAccounts] = useState()
  const [metadata, setMetadata] = useState()
  const [urlWebhook, setUrlWebhook] = useState()
  const [saveAccount, setSaveAccount] = useState(false)
  const [tikTokAccounts, setTikTokAccounts] = useState([])

  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)
  const [showModal, setShowModal] = useState()

  const [removingAccount, setRemovingAccount] = useState()
  const [editAccount, setEditAccount] = useState()
  const [newAccountData, setNewAccountData] = useState()

  const [isOpen, setIsOpen] = useState(false)
  const [message, setMessage] = useState('')

  const [defaultBMId, setDefaultBMId] = useState('')

  const [availableWabaToCatalog, setAvailableWabaToCatalog] = useState([])
  const [catalogList, setCatalogList] = useState([])

  const [errorOnCheck, setErrorOnCheck] = useState([])
  const [isCheckingCatalogId, setIsCheckingCatalogId] = useState([])

  const { data: wabas, refetch: refetchWabaList, isFetching: isFetchingWabaList } = useGetWabaList()
  const { data: accountsList } = useGetActivatedAccountsList()

  useEffect(() => {
    if (wabas?.length) {
      let withoutCatalog = []
      let withCatalog = []
      withoutCatalog = wabas
        .filter((item) => typeof item.catalog === 'undefined')
        .map((item) => ({
          id: item.id,
          title: item.name,
          numbers: item?.phoneNumbers || [],
          bmId: item?.bmId || '',
        }))
      withCatalog = wabas
        .filter((item) => !!item?.catalog)
        .map((item) => ({
          catalogId: item.catalog.id,
          catalogName: item.catalog.name,
          wabaId: item.id,
          wabaName: item.name,
          numbers: item?.phoneNumbers || [],
          bmId: item?.bmId || '',
          refreshDate: item.catalog?.refreshDate || null,
        }))
      setAvailableWabaToCatalog(withoutCatalog)
      setCatalogList(withCatalog)
    }
  }, [wabas])

  useEffect(() => {
    if (accountsList) {
      setAccounts(accountsList)
    }
  }, [accountsList])

  const refreshCatalogItem = useCallback(
    async ({ wabaId, catalogId }) => {
      setIsCheckingCatalogId((oldState) => [...oldState, catalogId])
      let hasError = false
      try {
        await api.refreshCatalog({ wabaId, catalogId })
      } catch (error) {
        hasError = true
        console.error(error)
      } finally {
        setIsCheckingCatalogId((oldState) => oldState.filter((item) => item !== catalogId))
        const catalogErrorOnCheck = errorOnCheck.filter((item) => item.catalogId === catalogId)[0] || null
        if (hasError) {
          if (!catalogErrorOnCheck) {
            setErrorOnCheck((oldState) => [...oldState, { wabaId, catalogId }])
          }
        } else {
          if (catalogErrorOnCheck) {
            setErrorOnCheck((oldState) => oldState.filter((item) => item.catalogId !== catalogId))
          }
          refetchWabaList()
        }
      }
    },
    [errorOnCheck, refetchWabaList],
  )

  useEffect(() => {
    ;(async () => {
      setIsLoadingAccounts(true)

      const responseBrokers = await api.getBrokers()
      setBrokers(responseBrokers.data)

      const responseChannels = await api.getChannels()
      setChannels(responseChannels.data)

      const responseDepartments = window.localStorage.getItem('departments')
      const myDepartments = responseDepartments ? JSON.parse(responseDepartments) : []
      const deps = myDepartments.filter((dep) => dep.title !== 'Todos os departamentos')
      setDepartments(deps)

      const res = await getTikTokAccounts()

      if (res.status === 'OK') {
        setTikTokAccounts(res.data?.filter((acc) => acc.active) || [])
      }

      setIsLoadingAccounts(false)

      const settingsResponse = await getSettings()
      setDefaultBMId(settingsResponse?.data?.defaultBMId || '')
    })()
  }, [])

  const setRemovingAccountAction = useCallback(({ account }) => {
    if (account) {
      setRemovingAccount(account)
      setShowModal('UI.MODAL.REMOVE_ACCOUNT')
    }
  }, [])

  const setEditAccountAction = useCallback(async ({ account }) => {
    if (account) {
      setEditAccount(account)
      setShowModal('UI.MODAL.EDIT_ACCOUNT')
      if (account.broker) {
        const responseMetadata = await api.getMetadata(account.broker)
        setMetadata(responseMetadata.data)
      }
    }
  }, [])

  const setAdvancedSettingsAction = useCallback(async ({ account }) => {
    if (account) {
      setEditAccount(account)
      setShowModal('UI.MODAL.ADVANCED_SETTINGS_ACCOUNT')
    }
  }, [])

  const setConfirmChangesAction = useCallback((editWithoutSaving) => {
    const result = editWithoutSaving()
    setNewAccountData(result)
    setShowModal('UI.MODAL.SAVE.ACCOUNT')
  }, [])

  const setConfirmDepartmentAction = useCallback((confirmDepartment) => {
    const result = confirmDepartment()
    setNewAccountData(result)
    setShowModal('UI.MODAL.CONFIRM_EDIT_DEPARTMENT')
  }, [])

  const setUrlWebhookAction = useCallback(({ account }) => {
    if (account) {
      setUrlWebhook(account)
      setShowModal('UI.MODAL.WEBHOOK')
    }
  }, [])

  const getChannel = useCallback(
    (id) => {
      const foundChannel = channels.find((channel) => channel.id === Number(id))
      if (!foundChannel) return null
      return foundChannel.name
    },
    [channels],
  )

  const getDepartments = useCallback(
    (id) => {
      const department = departments.find((departmentItem) => departmentItem.uuid === id)
      return department ? department.title : ''
    },
    [departments],
  )

  const setOpenSnackbar = useCallback((messageParam) => {
    setIsOpen(true)
    setMessage(messageParam)
  }, [])

  const setCloseSnackbar = useCallback(() => {
    setIsOpen(false)
    setMessage('')
  }, [])

  const removeTikTokAccount = useCallback(
    async (advertiserId) => {
      await revokeAccountConnection(advertiserId).then(() => {
        setTikTokAccounts([...tikTokAccounts.filter((acc) => acc.advertiserId !== advertiserId)])
      })
    },
    [tikTokAccounts],
  )

  const AccountsStateProviderValue = useMemo(
    () => ({
      state: {
        wabas,
        accounts,
        brokers,
        channels,
        departments,
        isLoadingAccounts,
        showModal,
        removingAccount,
        editAccount,
        urlWebhook,
        metadata,
        saveAccount,
        newAccountData,
        isOpen,
        message,
        catalogList,
        isLoadingCatalog: isFetchingWabaList,
        availableWabaToCatalog,
        defaultBMId,
        errorOnCheck,
        isCheckingCatalogId,
        tikTokAccounts,
      },
      actions: {
        setShowModal,
        setRemovingAccountAction,
        setUrlWebhookAction,
        setEditAccountAction,
        setAdvancedSettingsAction,
        setAccounts,
        setIsLoadingAccounts,
        setOpenSnackbar,
        setCloseSnackbar,
        setConfirmChangesAction,
        setConfirmDepartmentAction,
        setSaveAccount,
        setNewAccountData,
        getChannel,
        getDepartments,
        getCatalogList: refetchWabaList,
        refreshCatalogItem,
        setTikTokAccounts,
        removeTikTokAccount,
      },
    }),
    [
      wabas,
      accounts,
      brokers,
      channels,
      departments,
      isLoadingAccounts,
      showModal,
      removingAccount,
      editAccount,
      urlWebhook,
      metadata,
      saveAccount,
      newAccountData,
      isOpen,
      message,
      catalogList,
      isFetchingWabaList,
      availableWabaToCatalog,
      defaultBMId,
      errorOnCheck,
      isCheckingCatalogId,
      tikTokAccounts,
      setRemovingAccountAction,
      setUrlWebhookAction,
      setEditAccountAction,
      setAdvancedSettingsAction,
      setOpenSnackbar,
      setCloseSnackbar,
      setConfirmChangesAction,
      setConfirmDepartmentAction,
      getChannel,
      getDepartments,
      refetchWabaList,
      refreshCatalogItem,
      removeTikTokAccount,
    ],
  )

  return <AccountsContext.Provider value={AccountsStateProviderValue}>{children}</AccountsContext.Provider>
}

AccountsStateProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default AccountsStateProvider
